import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X, MonitorSmartphone } from "lucide-react";
import { CONTACTS, scrollToId } from "@/lib/site";

const LINKS = [
  { id: "servizi", label: "Servizi" },
  { id: "preventivo", label: "Preventivo" },
  { id: "chi-sono", label: "Chi Sono" },
  { id: "contatti", label: "Contatti" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled ? "bg-paper/85 backdrop-blur-xl border-line shadow-[0_8px_30px_rgba(18,24,32,0.06)]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <span className="w-9 h-9 rounded-lg bg-ink text-paper flex items-center justify-center transition-colors duration-300 group-hover:bg-copper">
            <MonitorSmartphone size={18} strokeWidth={2.2} />
          </span>
          <span className="font-display font-bold text-lg tracking-tight text-ink">
            Monte PC <span className="text-copper">Service</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => go(l.id)}
              className="nav-link font-mono text-xs uppercase tracking-[0.18em] text-smoke hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <span data-testid="nav-status-badge" className="hidden xl:flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-leaf">
            <span className="status-dot w-2 h-2 rounded-full bg-leaf inline-block" />
            Disponibile per interventi
          </span>
          <a
            data-testid="nav-phone-call-button"
            href={CONTACTS.phoneFixedHref}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-ink/15 font-mono text-xs text-ink hover:border-copper hover:text-copper transition-colors duration-300"
          >
            <Phone size={13} /> {CONTACTS.phoneFixed}
          </a>
          <a
            data-testid="nav-whatsapp-button"
            href={CONTACTS.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-paper font-mono text-xs hover:bg-copper transition-colors duration-300"
          >
            <MessageCircle size={13} /> WhatsApp
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-line text-ink"
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-paper/95 backdrop-blur-xl border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}-link`}
                  onClick={() => go(l.id)}
                  className="text-left font-display font-semibold text-xl text-ink"
                >
                  {l.label}
                </button>
              ))}
              <a data-testid="nav-mobile-whatsapp-button" href={CONTACTS.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-mono text-sm text-copper">
                <MessageCircle size={15} /> Scrivimi su WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
